/**
 * Колода Таро — единственный источник правды для клиента и сервера.
 * 78 карт: 22 старших аркана + 4 масти по 14 карт.
 *
 * Имена младших арканов собираются из масти и ранга («Туз Жезлов»),
 * потому что они действительно составные. Значения — таблица, потому что
 * они составными не являются.
 */

export type Lang = 'ru' | 'az' | 'en' | 'tr'

/** Языки в порядке показа в переключателе. */
export const LANGS: Lang[] = ['ru', 'az', 'en', 'tr']

/** Строка на всех языках. */
export type Loc = Record<Lang, string>

/**
 * Текст на нужном языке.
 *
 * Запасной вариант — английский, затем русский: пустая строка в интерфейсе
 * выглядит как поломка, а недопереведённая — как незаконченный перевод, что
 * ближе к правде. Пригодится и позже: языков будет больше, чем переводов.
 */
export function loc(v: Loc | undefined, lang: Lang): string {
  if (!v) return ''
  return v[lang] || v.en || v.ru || ''
}

/**
 * Приводит код языка (Telegram, браузер) к поддерживаемому.
 *
 * `fallback` — на случай незнакомого кода. По умолчанию английский, но на входе
 * в приложение передаётся русский: рынок СНГ, и человеку с немецким телефоном
 * русский ближе английского. Параметр появился потому, что раньше в четырёх
 * местах стояло `code === 'az' ? 'az' : 'ru'` — такая развилка знает только два
 * языка из четырёх и заводила турка с англичанином на русский.
 */
export function toLang(code: string | undefined, fallback: Lang = 'en'): Lang {
  const c = (code ?? '').slice(0, 2).toLowerCase()
  return (LANGS as string[]).includes(c) ? (c as Lang) : fallback
}

export type SuitId = 'wands' | 'cups' | 'swords' | 'pentacles'

export interface TarotCardDef {
  id: number
  arcana: 'major' | 'minor'
  suit?: SuitId
  /** Короткая метка на лицевой стороне: номер аркана или ранг карты. */
  rank: string
  sym: string
  ru: string
  az: string
  en: string
  tr: string
  /** Значение в прямом положении. */
  mRu: string
  mAz: string
  mEn: string
  mTr: string
  /** Значение в перевёрнутом положении. */
  rRu: string
  rAz: string
  rEn: string
  rTr: string
}

// ── Старшие арканы ───────────────────────────────────────────────────

const MAJOR: TarotCardDef[] = [
  { id: 0,  rank: '0',    sym: '✧', ru: 'Шут',             az: 'Dəli',
    mRu: 'начало пути, свобода и доверие к неизвестному', mAz: 'yolun başlanğıcı, azadlıq və naməluma inam',
    rRu: 'беспечность и страх сделать первый шаг',        rAz: 'ehtiyatsızlıq və ilk addımdan qorxu',
    en: 'The Fool', tr: 'Deli',
    mEn: 'the start of the road, freedom and trust in the unknown', mTr: 'yolun başlangıcı, özgürlük ve bilinmeyene güven',
    rEn: 'recklessness, and the fear of the first step', rTr: 'pervasızlık ve ilk adımı atma korkusu' },
  { id: 1,  rank: 'I',    sym: '✶', ru: 'Маг',             az: 'Sehrbaz',
    mRu: 'воля, новое начало и сила намерения',           mAz: 'iradə, yeni başlanğıc və niyyət gücü',
    rRu: 'распылённая энергия и самообман',               rAz: 'dağınıq enerji və özünü aldatma',
    en: 'The Magician', tr: 'Büyücü',
    mEn: 'will, a new beginning and the power of intent', mTr: 'irade, yeni bir başlangıç ve niyetin gücü',
    rEn: 'scattered energy and self-deception', rTr: 'dağılmış enerji ve kendini kandırma' },
  { id: 2,  rank: 'II',   sym: '☽', ru: 'Верховная Жрица', az: 'Baş Kahin',
    mRu: 'тайное знание и голос интуиции',                mAz: 'gizli bilik və intuisiyanın səsi',
    rRu: 'вы заглушили внутренний голос',                 rAz: 'daxili səsinizi susdurmusunuz',
    en: 'The High Priestess', tr: 'Başrahibe',
    mEn: 'hidden knowledge and the voice of intuition', mTr: 'gizli bilgi ve sezginin sesi',
    rEn: 'you have muffled your inner voice', rTr: 'iç sesinizi susturmuşsunuz' },
  { id: 3,  rank: 'III',  sym: '♛', ru: 'Императрица',     az: 'İmperatriça',
    mRu: 'изобилие, забота и творчество',                 mAz: 'bolluq, qayğı və yaradıcılıq',
    rRu: 'застой и забытая забота о себе',                rAz: 'durğunluq və unudulmuş özünə qayğı',
    en: 'The Empress', tr: 'İmparatoriçe',
    mEn: 'abundance, care and creation', mTr: 'bolluk, şefkat ve yaratıcılık',
    rEn: 'stagnation, and care for yourself forgotten', rTr: 'durgunluk ve unutulmuş öz bakım' },
  { id: 4,  rank: 'IV',   sym: '♚', ru: 'Император',       az: 'İmperator',
    mRu: 'структура, опора и здоровая власть',            mAz: 'struktur, dayaq və sağlam hakimiyyət',
    rRu: 'жёсткость там, где нужна гибкость',             rAz: 'çeviklik lazım olan yerdə sərtlik',
    en: 'The Emperor', tr: 'İmparator',
    mEn: 'structure, support and healthy authority', mTr: 'düzen, dayanak ve sağlıklı otorite',
    rEn: 'rigidity where flexibility is needed', rTr: 'esneklik gereken yerde katılık' },
  { id: 5,  rank: 'V',    sym: '✤', ru: 'Иерофант',        az: 'Kahin',
    mRu: 'традиция, наставник и верный ориентир',         mAz: 'ənənə, müəllim və düzgün istiqamət',
    rRu: 'чужие правила вместо своих',                    rAz: 'öz qaydalarınız əvəzinə başqasının',
    en: 'The Hierophant', tr: 'Aziz',
    mEn: 'tradition, a mentor and a true bearing', mTr: 'gelenek, bir yol gösterici ve doğru pusula',
    rEn: 'someone else\'s rules instead of your own', rTr: 'kendi kurallarınız yerine başkasınınki' },
  { id: 6,  rank: 'VI',   sym: '♡', ru: 'Влюблённые',      az: 'Aşiqlər',
    mRu: 'выбор сердца и глубокий союз',                  mAz: 'ürəyin seçimi və dərin birlik',
    rRu: 'сомнение и несогласие с собой',                 rAz: 'şübhə və özünüzlə razılaşmamaq',
    en: 'The Lovers', tr: 'Aşıklar',
    mEn: 'the choice of the heart and a deep union', mTr: 'kalbin seçimi ve derin bir birliktelik',
    rEn: 'doubt, and disagreement with yourself', rTr: 'şüphe ve kendinizle anlaşamama' },
  { id: 7,  rank: 'VII',  sym: '✹', ru: 'Колесница',       az: 'Araba',
    mRu: 'движение вперёд и победа воли',                 mAz: 'irəli hərəkət və iradənin qələbəsi',
    rRu: 'вы тянете в две стороны сразу',                 rAz: 'eyni anda iki tərəfə çəkirsiniz',
    en: 'The Chariot', tr: 'Savaş Arabası',
    mEn: 'moving forward and the victory of will', mTr: 'ileri hareket ve iradenin zaferi',
    rEn: 'you are pulling in two directions at once', rTr: 'aynı anda iki yöne çekiyorsunuz' },
  { id: 8,  rank: 'VIII', sym: '∞', ru: 'Сила',            az: 'Qüvvə',
    mRu: 'внутренняя стойкость и мягкая власть',          mAz: 'daxili dözüm və mülayim güc',
    rRu: 'сомнение в собственных силах',                  rAz: 'öz gücünüzə şübhə',
    en: 'Strength', tr: 'Güç',
    mEn: 'inner steadiness and gentle power', mTr: 'içsel dayanıklılık ve yumuşak güç',
    rEn: 'doubt in your own strength', rTr: 'kendi gücünüze duyduğunuz şüphe' },
  { id: 9,  rank: 'IX',   sym: '✵', ru: 'Отшельник',       az: 'Tənha',
    mRu: 'поиск истины и время для себя',                 mAz: 'həqiqət axtarışı və özün üçün vaxt',
    rRu: 'одиночество вместо уединения',                  rAz: 'təklik əvəzinə tənhalıq',
    en: 'The Hermit', tr: 'Ermiş',
    mEn: 'a search for truth and time for yourself', mTr: 'hakikat arayışı ve kendinize ayrılan zaman',
    rEn: 'loneliness instead of solitude', rTr: 'yalnızlık yerine kimsesizlik' },
  { id: 10, rank: 'X',    sym: '❂', ru: 'Колесо Фортуны',  az: 'Tale çarxı',
    mRu: 'перемены, судьба и новый виток',                mAz: 'dəyişiklik, tale və yeni dövr',
    rRu: 'сопротивление неизбежному',                     rAz: 'qaçılmaza müqavimət',
    en: 'Wheel of Fortune', tr: 'Kader Çarkı',
    mEn: 'change, fate and a new turn', mTr: 'değişim, kader ve yeni bir dönüş',
    rEn: 'resistance to the inevitable', rTr: 'kaçınılmaza direnmek' },
  { id: 11, rank: 'XI',   sym: '⚖', ru: 'Справедливость',  az: 'Ədalət',
    mRu: 'честный расчёт и ясное решение',                mAz: 'dürüst hesab və aydın qərar',
    rRu: 'вы избегаете честного разговора',               rAz: 'dürüst söhbətdən qaçırsınız',
    en: 'Justice', tr: 'Adalet',
    mEn: 'an honest reckoning and a clear decision', mTr: 'dürüst bir hesap ve net bir karar',
    rEn: 'you are avoiding an honest conversation', rTr: 'dürüst bir konuşmadan kaçıyorsunuz' },
  { id: 12, rank: 'XII',  sym: '⧗', ru: 'Повешенный',      az: 'Asılmış',
    mRu: 'пауза, что меняет угол зрения',                 mAz: 'baxış bucağını dəyişən fasilə',
    rRu: 'затянувшееся ожидание без смысла',              rAz: 'mənasız uzanan gözləmə',
    en: 'The Hanged Man', tr: 'Asılan Adam',
    mEn: 'a pause that changes the angle of sight', mTr: 'bakış açısını değiştiren bir duraklama',
    rEn: 'waiting that has dragged on without meaning', rTr: 'anlamsızca uzayan bir bekleyiş' },
  { id: 13, rank: 'XIII', sym: '⚱', ru: 'Смерть',          az: 'Ölüm',
    mRu: 'завершение, освобождающее место новому',        mAz: 'yeniyə yer açan tamamlanma',
    rRu: 'вы держитесь за то, что уже ушло',              rAz: 'artıq getmiş şeydən yapışırsınız',
    en: 'Death', tr: 'Ölüm',
    mEn: 'an ending that makes room for the new', mTr: 'yeniye yer açan bir son',
    rEn: 'you are holding on to what has already gone', rTr: 'çoktan gitmiş olana tutunuyorsunuz' },
  { id: 14, rank: 'XIV',  sym: '⚗', ru: 'Умеренность',     az: 'Mötədillik',
    mRu: 'баланс, терпение и верная мера',                mAz: 'tarazlıq, səbir və düzgün ölçü',
    rRu: 'крайности вместо середины',                     rAz: 'ortalıq əvəzinə ifrat',
    en: 'Temperance', tr: 'Denge',
    mEn: 'balance, patience and the right measure', mTr: 'denge, sabır ve doğru ölçü',
    rEn: 'extremes instead of the middle', rTr: 'orta yol yerine aşırılık' },
  { id: 15, rank: 'XV',   sym: '♆', ru: 'Дьявол',          az: 'Şeytan',
    mRu: 'привязанность, страсть и её цена',              mAz: 'bağlılıq, ehtiras və onun qiyməti',
    rRu: 'цепи спадают — вы видите правду',               rAz: 'zəncirlər qırılır — həqiqəti görürsünüz',
    en: 'The Devil', tr: 'Şeytan',
    mEn: 'attachment, passion and its price', mTr: 'bağlanma, tutku ve onun bedeli',
    rEn: 'the chains fall away — you see the truth', rTr: 'zincirler çözülüyor — gerçeği görüyorsunuz' },
  { id: 16, rank: 'XVI',  sym: '⚡', ru: 'Башня',           az: 'Qüllə',
    mRu: 'внезапные перемены и освобождение',             mAz: 'qəfil dəyişiklik və azadlıq',
    rRu: 'кризис, которого удалось избежать',             rAz: 'qaçılan böhran',
    en: 'The Tower', tr: 'Kule',
    mEn: 'sudden change and release', mTr: 'ani değişim ve özgürleşme',
    rEn: 'a crisis that was avoided in time', rTr: 'zamanında atlatılmış bir kriz' },
  { id: 17, rank: 'XVII', sym: '✦', ru: 'Звезда',          az: 'Ulduz',
    mRu: 'надежда, вдохновение и вера в будущее',         mAz: 'ümid, ilham və gələcəyə inam',
    rRu: 'вера потускнела, но не исчезла',                rAz: 'inam solub, amma itməyib',
    en: 'The Star', tr: 'Yıldız',
    mEn: 'hope, inspiration and faith in what is ahead', mTr: 'umut, ilham ve geleceğe inanç',
    rEn: 'the faith has dimmed, but has not gone out', rTr: 'inanç solmuş ama sönmemiş' },
  { id: 18, rank: 'XVIII',sym: '☾', ru: 'Луна',            az: 'Ay',
    mRu: 'интуиция, тайны и скрытые чувства',             mAz: 'intuisiya, sirlər və gizli hisslər',
    rRu: 'туман рассеивается, страх отступает',           rAz: 'duman dağılır, qorxu geri çəkilir',
    en: 'The Moon', tr: 'Ay',
    mEn: 'intuition, secrets and hidden feelings', mTr: 'sezgi, sırlar ve gizli duygular',
    rEn: 'the fog is lifting, the fear is receding', rTr: 'sis dağılıyor, korku geri çekiliyor' },
  { id: 19, rank: 'XIX',  sym: '☀', ru: 'Солнце',          az: 'Günəş',
    mRu: 'радость, ясность и заслуженный успех',          mAz: 'sevinc, aydınlıq və haqq edilmiş uğur',
    rRu: 'радость есть, но вы её не замечаете',           rAz: 'sevinc var, amma onu görmürsünüz',
    en: 'The Sun', tr: 'Güneş',
    mEn: 'joy, clarity and success you have earned', mTr: 'sevinç, berraklık ve hak edilmiş başarı',
    rEn: 'the joy is there, but you do not notice it', rTr: 'sevinç var ama onu fark etmiyorsunuz' },
  { id: 20, rank: 'XX',   sym: '⚜', ru: 'Суд',             az: 'Hökm',
    mRu: 'зов, пробуждение и честный итог',               mAz: 'çağırış, oyanış və dürüst nəticə',
    rRu: 'вы откладываете важное решение',                rAz: 'vacib qərarı təxirə salırsınız',
    en: 'Judgement', tr: 'Mahkeme',
    mEn: 'a call, an awakening and an honest summing-up', mTr: 'bir çağrı, uyanış ve dürüst bir sonuç',
    rEn: 'you keep putting off an important decision', rTr: 'önemli bir kararı erteliyorsunuz' },
  { id: 21, rank: 'XXI',  sym: '⊕', ru: 'Мир',             az: 'Dünya',
    mRu: 'завершение цикла и целостность',                mAz: 'dövrün tamamlanması və bütövlük',
    rRu: 'финал близок, но ещё не наступил',              rAz: 'final yaxındır, amma hələ gəlməyib',
    en: 'The World', tr: 'Dünya',
    mEn: 'a cycle completed and wholeness', mTr: 'bir döngünün tamamlanması ve bütünlük',
    rEn: 'the finale is near, but has not arrived', rTr: 'final yakın ama henüz gelmedi' },
].map((c) => ({ ...c, arcana: 'major' as const }))

// ── Масти и ранги младших арканов ────────────────────────────────────

interface SuitDef {
  id: SuitId
  /** Символ масти; совпадает с игральными: жезлы ♣, кубки ♥, мечи ♠, пентакли ♦. */
  sym: string
  ru: string
  az: string
  /** Родительный падеж для имени карты: «Туз Жезлов». */
  ruGen: string
  /** Изафетная форма: «Əsaların Ası». */
  azGen: string
  en: string
  tr: string
  /** В английском имя строится через «of»: «Ace of Wands». */
  enGen: string
  /** В турецком изафет, как и в азербайджанском: «Değneklerin Ası». */
  trGen: string
}

export const SUITS: SuitDef[] = [
  { id: 'wands',     sym: '♣', ru: 'Жезлы',    az: 'Əsalar',     ruGen: 'Жезлов',    azGen: 'Əsaların',
    en: 'Wands',     tr: 'Değnekler', enGen: 'Wands',     trGen: 'Değneklerin' },
  { id: 'cups',      sym: '♥', ru: 'Кубки',    az: 'Camlar',     ruGen: 'Кубков',    azGen: 'Camların',
    en: 'Cups',      tr: 'Kupalar',   enGen: 'Cups',      trGen: 'Kupaların' },
  { id: 'swords',    sym: '♠', ru: 'Мечи',     az: 'Qılınclar',  ruGen: 'Мечей',     azGen: 'Qılıncların',
    en: 'Swords',    tr: 'Kılıçlar',  enGen: 'Swords',    trGen: 'Kılıçların' },
  { id: 'pentacles', sym: '♦', ru: 'Пентакли', az: 'Pentakllar', ruGen: 'Пентаклей', azGen: 'Pentaklların',
    en: 'Pentacles', tr: 'Tılsımlar', enGen: 'Pentacles', trGen: 'Tılsımların' },
]

interface RankDef {
  /** Метка на карте — короткая и не зависящая от языка. */
  label: string
  ru: string
  /** Азербайджанский с притяжательным суффиксом: «Əsaların Ası». */
  az: string
  en: string
  /** Турецкий тоже с притяжательным суффиксом: «Değneklerin Ası». */
  tr: string
}

const RANKS: RankDef[] = [
  { label: 'A',  ru: 'Туз',       az: 'Ası', en: 'Ace', tr: 'Ası' },
  { label: '2',  ru: 'Двойка',    az: 'İkiliyi', en: 'Two', tr: 'İkilisi' },
  { label: '3',  ru: 'Тройка',    az: 'Üçlüyü', en: 'Three', tr: 'Üçlüsü' },
  { label: '4',  ru: 'Четвёрка',  az: 'Dördlüyü', en: 'Four', tr: 'Dörtlüsü' },
  { label: '5',  ru: 'Пятёрка',   az: 'Beşliyi', en: 'Five', tr: 'Beşlisi' },
  { label: '6',  ru: 'Шестёрка',  az: 'Altılığı', en: 'Six', tr: 'Altılısı' },
  { label: '7',  ru: 'Семёрка',   az: 'Yeddiliyi', en: 'Seven', tr: 'Yedilisi' },
  { label: '8',  ru: 'Восьмёрка', az: 'Səkkizliyi', en: 'Eight', tr: 'Sekizlisi' },
  { label: '9',  ru: 'Девятка',   az: 'Doqquzluğu', en: 'Nine', tr: 'Dokuzlusu' },
  { label: '10', ru: 'Десятка',   az: 'Onluğu', en: 'Ten', tr: 'Onlusu' },
  { label: 'P',  ru: 'Паж',       az: 'Pajı', en: 'Page', tr: 'Prensi' },
  { label: 'N',  ru: 'Рыцарь',    az: 'Cəngavəri', en: 'Knight', tr: 'Şövalyesi' },
  { label: 'Q',  ru: 'Королева',  az: 'Kraliçası', en: 'Queen', tr: 'Kraliçesi' },
  { label: 'K',  ru: 'Король',    az: 'Kralı', en: 'King', tr: 'Kralı' },
]

type Meaning = [mRu: string, rRu: string, mAz: string, rAz: string,
  mEn: string, rEn: string, mTr: string, rTr: string]

/** Значения младших арканов: 14 рангов на масть, в порядке RANKS. */
const MINOR_MEANINGS: Record<SuitId, Meaning[]> = {
  wands: [
    ['вспышка вдохновения и новое начало', 'замысел, который никак не начнётся',
     'ilham qığılcımı və yeni başlanğıc', 'başlaya bilməyən niyyət',
     'a spark of inspiration and a new beginning', 'an idea that never quite starts',
     'bir ilham kıvılcımı ve yeni bir başlangıç', 'bir türlü başlamayan bir fikir'],
    ['первый выбор пути и взгляд вдаль', 'страх выйти за пределы привычного',
     'yolun ilk seçimi və uzaqlara baxış', 'tanış hüdudlardan çıxmaq qorxusu',
     'the first choice of road and a look into the distance', 'the fear of stepping past the familiar',
     'yolun ilk seçimi ve uzaklara bakış', 'alışılmışın dışına çıkma korkusu'],
    ['замысел пошёл в рост, горизонт открыт', 'ожидание затянулось, планы буксуют',
     'niyyət böyüyür, üfüq açıqdır', 'gözləmə uzandı, planlar yerində sayır',
     'the plan has taken root, the horizon is open', 'the waiting drags on, plans stall',
     'tasarı büyümeye başladı, ufuk açık', 'bekleyiş uzadı, planlar yerinde sayıyor'],
    ['праздник, дом и заслуженная передышка', 'радость есть, но её не с кем разделить',
     'bayram, ev və haqq edilmiş nəfəs', 'sevinc var, amma bölüşməyə adam yoxdur',
     'celebration, home and a rest you have earned', 'there is joy, but no one to share it with',
     'kutlama, ev ve hak edilmiş bir mola', 'sevinç var ama paylaşacak kimse yok'],
    ['спор, соперничество и проба сил', 'конфликт утих, но осадок остался',
     'mübahisə, rəqabət və güc sınağı', 'münaqişə səngidi, amma izi qaldı',
     'dispute, rivalry and a test of strength', 'the conflict has died down, but left a residue',
     'tartışma, rekabet ve bir güç sınavı', 'çatışma dindi ama tortusu kaldı'],
    ['признание и заслуженная победа', 'похвала запаздывает, вера в себя тускнеет',
     'tanınma və haqq edilmiş qələbə', 'tərif gecikir, özünə inam solur',
     'recognition and a victory you have earned', 'the praise is late, faith in yourself dims',
     'tanınma ve hak edilmiş bir zafer', 'övgü gecikiyor, kendine güven soluyor'],
    ['вы держите оборону и стоите на своём', 'силы на исходе, пора выбрать бой',
     'müdafiə saxlayır və sözünüzdə durursunuz', 'güc tükənir, döyüşü seçmək vaxtıdır',
     'you hold the line and stand your ground', 'your strength is running out, choose your battle',
     'savunmayı tutuyor ve sözünüzde duruyorsunuz', 'güç tükeniyor, savaşını seçme vakti'],
    ['события ускоряются, новости в пути', 'задержки и сорванные сроки',
     'hadisələr sürətlənir, xəbər yoldadır', 'gecikmələr və pozulan müddətlər',
     'events speed up, news is on its way', 'delays and deadlines that slip',
     'olaylar hızlanıyor, haber yolda', 'gecikmeler ve kaçan teslim tarihleri'],
    ['последнее усилие, вы устали, но держитесь', 'усталость победила осторожность',
     'son səy — yorğunsunuz, amma dözürsünüz', 'yorğunluq ehtiyatı üstələdi',
     'one last effort — you are tired but holding on', 'weariness has beaten caution',
     'son bir çaba — yorgunsunuz ama dayanıyorsunuz', 'yorgunluk ihtiyatı yendi'],
    ['вы взвалили на себя слишком много', 'пора отпустить чужую ношу',
     'çiyninizə həddindən artıq yük götürmüsünüz', 'özgə yükünü buraxmaq vaxtıdır',
     'you have taken far too much on yourself', 'it is time to put down someone else\'s load',
     'omzunuza fazlasıyla yük almışsınız', 'başkasının yükünü bırakma vakti'],
    ['любопытство и первая проба нового дела', 'энтузиазм гаснет быстрее, чем разгорелся',
     'maraq və yeni işin ilk sınağı', 'həvəs alovlandığından tez sönür',
     'curiosity and the first try at something new', 'the enthusiasm fades faster than it flared',
     'merak ve yeni bir işin ilk denemesi', 'heves alevlendiğinden hızlı sönüyor'],
    ['смелый рывок и жажда действия', 'спешка без плана и расплата за неё',
     'cəsarətli atılım və hərəkət susuzluğu', 'plansız tələsmə və onun bədəli',
     'a bold dash and a thirst for action', 'haste without a plan, and the price of it',
     'cesur bir atılım ve harekete duyulan susuzluk', 'plansız acele ve bedeli'],
    ['уверенность, тепло и притягательная сила', 'ревность и потребность в признании',
     'inam, istilik və cazibə gücü', 'qısqanclıq və tanınma ehtiyacı',
     'confidence, warmth and a magnetic pull', 'jealousy and a need to be recognised',
     'güven, sıcaklık ve çekim gücü', 'kıskançlık ve tanınma ihtiyacı'],
    ['воля вести за собой и видеть цель', 'властность вместо лидерства',
     'arxasınca aparmaq iradəsi və məqsədi görmək', 'liderlik əvəzinə hökmranlıq',
     'the will to lead and to keep the goal in sight', 'domineering instead of leading',
     'önden gitme iradesi ve hedefi görmek', 'liderlik yerine buyurganlık'],
  ],
  cups: [
    ['сердце открывается, чувство рождается', 'чувство есть, но выразить его трудно',
     'ürək açılır, hiss doğulur', 'hiss var, amma onu ifadə etmək çətindir',
     'the heart opens, a feeling is born', 'the feeling is there, but hard to put into words',
     'kalp açılıyor, bir duygu doğuyor', 'duygu var ama ifade etmek zor'],
    ['союз, взаимность и честный обмен', 'неравенство в отношениях',
     'birlik, qarşılıqlılıq və dürüst mübadilə', 'münasibətlərdə bərabərsizlik',
     'a union, mutuality and an honest exchange', 'an imbalance in the relationship',
     'birliktelik, karşılıklılık ve dürüst bir alışveriş', 'ilişkideki eşitsizlik'],
    ['дружба, радость и общий праздник', 'круг стал теснее, чем хотелось',
     'dostluq, sevinc və ortaq bayram', 'dairə istədiyinizdən dar oldu',
     'friendship, joy and a shared celebration', 'the circle has grown tighter than you wanted',
     'dostluk, sevinç ve ortak bir kutlama', 'çevre istediğinizden dar kaldı'],
    ['пресыщение и скука среди изобилия', 'вы наконец замечаете предложенное',
     'bolluq içində doyma və darıxma', 'nəhayət təklif olunanı görürsünüz',
     'surfeit and boredom in the middle of plenty', 'you finally notice what is being offered',
     'bolluk içinde doygunluk ve sıkıntı', 'sonunda size sunulanı fark ediyorsunuz'],
    ['печаль о том, что пролито и ушло', 'взгляд поворачивается к тому, что осталось',
     'tökülənə və gedənə görə kədər', 'baxış qalana tərəf dönür',
     'grief for what was spilled and lost', 'the gaze turns to what remains',
     'dökülene ve gidene duyulan hüzün', 'bakış kalana dönüyor'],
    ['тёплая память и голос детства', 'прошлое держит крепче, чем нужно',
     'isti xatirə və uşaqlığın səsi', 'keçmiş lazım olduğundan bərk tutur',
     'a warm memory and the voice of childhood', 'the past holds tighter than it should',
     'sıcak bir anı ve çocukluğun sesi', 'geçmiş gerektiğinden sıkı tutuyor'],
    ['множество соблазнов и туман иллюзий', 'туман расходится, выбор становится ясным',
     'çoxlu şirnikdirmə və illüziya dumanı', 'duman dağılır, seçim aydınlaşır',
     'many temptations and a fog of illusions', 'the fog clears, the choice becomes plain',
     'birçok cazibe ve yanılsama sisi', 'sis dağılıyor, seçim netleşiyor'],
    ['вы уходите от того, что больше не питает', 'уйти не получается, вы возвращаетесь',
     'artıq qidalandırmayandan uzaqlaşırsınız', 'gedə bilmirsiniz, geri qayıdırsınız',
     'you walk away from what no longer feeds you', 'leaving does not work, you come back',
     'artık sizi beslemeyenden uzaklaşıyorsunuz', 'gitmek olmuyor, geri dönüyorsunuz'],
    ['желание сбывается, довольство собой', 'желаемое получено, радости меньше ожидаемой',
     'arzu gerçəkləşir, özünüzdən razısınız', 'istədiyiniz oldu, sevinc gözlədiyinizdən azdır',
     'a wish comes true and you are pleased with yourself', 'you got what you wanted, and it gladdens you less than expected',
     'dilek gerçekleşiyor, kendinizden memnunsunuz', 'istediğiniz oldu ama sevinç beklediğinizden az'],
    ['полнота чувств и мир в доме', 'картинка счастья не совпадает с ощущением',
     'hisslərin dolğunluğu və evdə dinclik', 'xoşbəxtlik şəkli hisslə üst-üstə düşmür',
     'fullness of feeling and peace at home', 'the picture of happiness does not match the feeling',
     'duyguların doluluğu ve evdeki huzur', 'mutluluk tablosu hissettiğinizle örtüşmüyor'],
    ['нежность, мечтательность и первое признание', 'обидчивость и уход в фантазии',
     'incəlik, xəyalpərəstlik və ilk etiraf', 'küskünlük və xəyallara qaçış',
     'tenderness, daydreaming and a first confession', 'touchiness and a retreat into fantasy',
     'incelik, hayalperestlik ve ilk itiraf', 'alınganlık ve hayallere kaçış'],
    ['романтический порыв и следование зову сердца', 'обещания красивее поступков',
     'romantik təkan və ürəyin səsinə getmək', 'vədlər əməllərdən gözəldir',
     'a romantic impulse and following the heart', 'the promises are prettier than the deeds',
     'romantik bir dürtü ve kalbin sesine uymak', 'sözler eylemlerden güzel'],
    ['глубокое сочувствие и умение чувствовать других', 'чужая боль вытеснила собственную',
     'dərin şəfqət və başqalarını duymaq bacarığı', 'özgə ağrısı öz ağrınızı sıxışdırdı',
     'deep compassion and a gift for feeling others', 'someone else\'s pain has crowded out your own',
     'derin şefkat ve başkalarını hissetme yeteneği', 'başkasının acısı kendi acınızı bastırdı'],
    ['зрелое чувство и власть над своими эмоциями', 'чувства спрятаны так глубоко, что не слышны',
     'yetkin hiss və emosiyalara hakimlik', 'hisslər elə dərin gizlənib ki, eşidilmir',
     'mature feeling and command of your own emotions', 'the feelings are hidden so deep they cannot be heard',
     'olgun duygu ve duygulara hâkimiyet', 'duygular öyle derine saklanmış ki duyulmuyor'],
  ],
  swords: [
    ['ясность мысли и решающее слово', 'правда произнесена не вовремя',
     'fikrin aydınlığı və həlledici söz', 'həqiqət vaxtsız deyildi',
     'clarity of thought and the decisive word', 'the truth was spoken at the wrong moment',
     'düşüncenin berraklığı ve belirleyici söz', 'gerçek yanlış zamanda söylendi'],
    ['вы избегаете выбора и закрываете глаза', 'повязка спадает, решать придётся',
     'seçimdən qaçır və gözünüzü yumursunuz', 'sarğı düşür, qərar verməli olacaqsınız',
     'you avoid the choice and keep your eyes shut', 'the blindfold slips, the decision must be made',
     'seçimden kaçıyor ve gözlerinizi kapıyorsunuz', 'bağ çözülüyor, karar vermek gerekecek'],
    ['слова ранят, разлука причиняет боль', 'рана затягивается, боль отпускает',
     'sözlər yaralayır, ayrılıq ağrıdır', 'yara sağalır, ağrı buraxır',
     'words wound, parting hurts', 'the wound is closing, the pain lets go',
     'sözler yaralıyor, ayrılık acıtıyor', 'yara kapanıyor, acı geçiyor'],
    ['пауза, тишина и восстановление сил', 'отдых откладывается снова и снова',
     'fasilə, sükut və gücün bərpası', 'istirahət təkrar-təkrar təxirə düşür',
     'a pause, quiet and the return of strength', 'the rest is put off again and again',
     'duraklama, sessizlik ve gücün toplanması', 'dinlenme defalarca erteleniyor'],
    ['победа, за которую заплачено отношениями', 'время признать цену и отступить',
     'münasibətlərlə ödənilmiş qələbə', 'qiyməti etiraf edib geri çəkilmək vaxtıdır',
     'a victory paid for with a relationship', 'time to admit the price and step back',
     'bir ilişkiyle bedeli ödenmiş bir zafer', 'bedeli kabul edip geri çekilme vakti'],
    ['переход к спокойным водам', 'вы везёте старую тяжесть на новое место',
     'sakit sulara keçid', 'köhnə yükü yeni yerə daşıyırsınız',
     'a crossing into calmer waters', 'you are carrying the old weight to a new place',
     'daha sakin sulara geçiş', 'eski yükü yeni bir yere taşıyorsunuz'],
    ['хитрость, недосказанность и обходной путь', 'тайное становится явным',
     'hiylə, deyilməmiş söz və dolayı yol', 'gizli olan aşkara çıxır',
     'cunning, half-said things and a way around', 'what was hidden comes to light',
     'kurnazlık, yarım sözler ve dolambaçlı yol', 'gizli olan açığa çıkıyor'],
    ['вы чувствуете себя связанным, хотя узлы не туги', 'вы делаете первый шаг из клетки',
     'özünüzü bağlı hiss edirsiniz, halbuki düyünlər boşdur', 'qəfəsdən ilk addımı atırsınız',
     'you feel bound, though the knots are loose', 'you take the first step out of the cage',
     'bağlı hissediyorsunuz, oysa düğümler gevşek', 'kafesten ilk adımı atıyorsunuz'],
    ['тревога, что не даёт спать', 'страх оказался больше опасности',
     'yatmağa qoymayan narahatlıq', 'qorxu təhlükədən böyük çıxdı',
     'the worry that will not let you sleep', 'the fear turned out to be larger than the danger',
     'uyutmayan kaygı', 'korku tehlikeden büyük çıktı'],
    ['конец, который больно признать', 'худшее позади, начинается рассвет',
     'etiraf etməsi ağrılı olan son', 'ən pisi arxada qaldı, dan yeri sökülür',
     'an ending that is painful to admit', 'the worst is behind, the dawn is starting',
     'kabul etmesi acı veren bir son', 'en kötüsü geride, şafak başlıyor'],
    ['наблюдательность и жажда докопаться до сути', 'слова опережают понимание',
     'müşahidəçilik və mahiyyətə varmaq həvəsi', 'sözlər anlayışı qabaqlayır',
     'sharp eyes and a hunger to get to the bottom of it', 'words run ahead of understanding',
     'keskin bir gözlem ve işin özüne inme isteği', 'sözler anlayışın önüne geçiyor'],
    ['стремительный натиск и прямота', 'резкость рушит то, что строили',
     'sürətli hücum və birbaşalıq', 'kəskinlik qurulanı dağıdır',
     'a swift charge and plain speaking', 'sharpness breaks what was being built',
     'hızlı bir hamle ve dobralık', 'sertlik kurulanı yıkıyor'],
    ['трезвый ум и честность без прикрас', 'холодность вместо ясности',
     'ayıq ağıl və bəzəksiz dürüstlük', 'aydınlıq əvəzinə soyuqluq',
     'a sober mind and honesty without varnish', 'coldness instead of clarity',
     'ayık bir zihin ve süssüz dürüstlük', 'berraklık yerine soğukluk'],
    ['закон, логика и справедливое решение', 'строгость без снисхождения',
     'qanun, məntiq və ədalətli qərar', 'güzəştsiz sərtlik',
     'law, logic and a just decision', 'severity without any mercy',
     'yasa, mantık ve adil bir karar', 'merhametsiz bir katılık'],
  ],
  pentacles: [
    ['надёжная возможность и первый вклад', 'шанс упущен из-за медлительности',
     'etibarlı imkan və ilk töhfə', 'şans ləngliyə görə əldən çıxdı',
     'a solid opportunity and a first investment', 'the chance slipped away through slowness',
     'sağlam bir fırsat ve ilk yatırım', 'ağırdan alındığı için kaçan şans'],
    ['жонглирование делами и хрупкий баланс', 'одно из дел придётся отпустить',
     'işlərlə hoqqabazlıq və kövrək tarazlıq', 'işlərdən birini buraxmalı olacaqsınız',
     'juggling matters and a fragile balance', 'one of the matters will have to be let go',
     'işleri idare etme ve kırılgan bir denge', 'işlerden birini bırakmak gerekecek'],
    ['мастерство, признанное другими', 'вклад не оценили по достоинству',
     'başqaları tərəfindən tanınan ustalıq', 'töhfə layiqincə dəyərləndirilmədi',
     'craft that others acknowledge', 'the contribution was not valued as it deserved',
     'başkalarınca tanınan ustalık', 'emek hak ettiği gibi değerlendirilmedi'],
    ['вы держите нажитое крепко', 'хватка ослабевает, и это к лучшему',
     'qazandığınızı bərk tutursunuz', 'əl boşalır və bu yaxşılığadır',
     'you hold what you have gathered tightly', 'the grip is loosening, and that is for the better',
     'kazandığınızı sıkı tutuyorsunuz', 'tutuş gevşiyor ve bu iyiye'],
    ['нужда и чувство, что вы за дверью', 'помощь ближе, чем кажется',
     'ehtiyac və qapı arxasında qalmaq hissi', 'kömək göründüyündən yaxındır',
     'need, and the feeling of standing outside the door', 'help is closer than it seems',
     'yokluk ve kapının dışında kalma hissi', 'yardım göründüğünden yakın'],
    ['щедрость и справедливый обмен', 'помощь с условием, а не от сердца',
     'səxavət və ədalətli mübadilə', 'ürəkdən deyil, şərtlə kömək',
     'generosity and a fair exchange', 'help with a condition, not from the heart',
     'cömertlik ve adil bir alışveriş', 'gönülden değil, şartlı bir yardım'],
    ['пауза, чтобы оценить всходы', 'нетерпение мешает урожаю созреть',
     'cücərtiləri qiymətləndirmək üçün fasilə', 'səbirsizlik məhsulun yetişməsinə mane olur',
     'a pause to look at what has grown', 'impatience keeps the harvest from ripening',
     'yetişeni değerlendirmek için bir mola', 'sabırsızlık hasadın olgunlaşmasını engelliyor'],
    ['терпеливый труд и рост мастерства', 'работа стала рутиной без смысла',
     'səbirli əmək və ustalığın artması', 'iş mənasız gündəlik təkrara döndü',
     'patient work and craft that grows', 'the work has become routine without meaning',
     'sabırlı emek ve gelişen ustalık', 'iş anlamsız bir rutine dönüştü'],
    ['самостоятельность и заслуженный достаток', 'достаток есть, свободы меньше',
     'müstəqillik və haqq edilmiş bolluq', 'bolluq var, azadlıq azdır',
     'self-reliance and a comfort you have earned', 'the comfort is there, the freedom is less',
     'kendi ayakları üstünde durmak ve hak edilmiş refah', 'refah var ama özgürlük az'],
    ['устойчивость, семья и наследие', 'спор о том, что нажито вместе',
     'sabitlik, ailə və miras', 'birlikdə qazanılan barədə mübahisə',
     'stability, family and what is passed on', 'an argument about what was built together',
     'istikrar, aile ve miras', 'birlikte kazanılan üzerine bir tartışma'],
    ['прилежание и первый шаг к делу', 'замысел так и остался на бумаге',
     'səy və işə doğru ilk addım', 'niyyət kağız üzərində qaldı',
     'diligence and the first step into the work', 'the plan stayed on paper',
     'gayret ve işe atılan ilk adım', 'tasarı kâğıt üstünde kaldı'],
    ['методичность и верность взятому темпу', 'осторожность превратилась в застой',
     'ardıcıllıq və seçilmiş tempə sadiqlik', 'ehtiyat durğunluğa çevrildi',
     'method and keeping the pace you took', 'caution has turned into standing still',
     'yöntem ve tutulan tempoya sadakat', 'ihtiyat yerinde saymaya dönüştü'],
    ['забота, практичность и хозяйское тепло', 'забота о других вместо себя',
     'qayğı, praktiklik və ev istiliyi', 'özünüz əvəzinə başqalarına qayğı',
     'care, practicality and a homely warmth', 'care for others instead of yourself',
     'şefkat, pratiklik ve ev sıcaklığı', 'kendiniz yerine başkalarına bakmak'],
    ['устойчивое благополучие и щедрая опора', 'всё измеряется деньгами',
     'sabit rifah və səxavətli dayaq', 'hər şey pulla ölçülür',
     'steady well-being and a generous support', 'everything is measured in money',
     'sağlam bir refah ve cömert bir dayanak', 'her şey parayla ölçülüyor'],
  ],
}

const MINOR: TarotCardDef[] = SUITS.flatMap((suit, suitIndex) =>
  RANKS.map((rank, rankIndex) => {
    const [mRu, rRu, mAz, rAz, mEn, rEn, mTr, rTr] = MINOR_MEANINGS[suit.id][rankIndex]
    return {
      id: 22 + suitIndex * RANKS.length + rankIndex,
      arcana: 'minor' as const,
      suit: suit.id,
      rank: rank.label,
      sym: suit.sym,
      ru: `${rank.ru} ${suit.ruGen}`,
      az: `${suit.azGen} ${rank.az}`,
      en: `${rank.en} of ${suit.enGen}`,
      tr: `${suit.trGen} ${rank.tr}`,
      mRu,
      mAz,
      mEn,
      mTr,
      rRu,
      rAz,
      rEn,
      rTr,
    }
  }),
)

/** Полная колода: 22 старших аркана + 56 младших. */
export const CARDS: TarotCardDef[] = [...MAJOR, ...MINOR]

export const DECK_SIZE = CARDS.length

// ── Помощники ────────────────────────────────────────────────────────

/**
 * На каком языке называются карты.
 *
 * **В азербайджанской версии названия карт турецкие, и это не недоделка.** Таро
 * пришло в Азербайджан через турецкий, и людям знакомы именно турецкие имена:
 * азербайджанские кальки («Camların Kraliçası») не узнаются вовсе — сказано
 * носителями. Всё остальное на азербайджанском остаётся азербайджанским:
 * значения карт, подписи позиций, интерфейс и сам текст толкования. Турецкие
 * тут только имена.
 *
 * Правило живёт одной функцией, потому что называют карты в тридцати с лишним
 * местах — экраны, доски, уведомление о карте дня, карточка «поделиться»,
 * промпт модели, рекламные карточки. Развилка `lang === 'az'` в каждом из них
 * разошлась бы при первой же правке.
 */
export function nameLang(lang: Lang): Lang {
  return lang === 'az' ? 'tr' : lang
}

export function cardName(card: TarotCardDef, lang: Lang): string {
  return loc(card, nameLang(lang))
}

export function cardMeaning(card: TarotCardDef, lang: Lang, reversed: boolean): string {
  const pick = (ru: string, az: string, en: string, tr: string) =>
    loc({ ru, az, en, tr }, lang)
  if (reversed) return pick(card.rRu, card.rAz, card.rEn, card.rTr)
  return pick(card.mRu, card.mAz, card.mEn, card.mTr)
}

export function cardById(id: number): TarotCardDef {
  return CARDS[id] ?? CARDS[0]
}

/** Масть — часть имени карты, поэтому язык у неё тот же (см. `nameLang`). */
export function suitName(suit: SuitId, lang: Lang): string {
  const s = SUITS.find((x) => x.id === suit)
  return s ? loc(s, nameLang(lang)) : ''
}

/**
 * Детерминированный расклад: одинаковый seed → одинаковые карты.
 * Нужен, чтобы «карта дня» не менялась при перезагрузке и чтобы
 * бэкенд и клиент видели один и тот же расклад.
 */
export function drawCards(seed: string, count: number): { id: number; reversed: boolean }[] {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  const rand = () => {
    h ^= h << 13
    h ^= h >>> 17
    h ^= h << 5
    return ((h >>> 0) % 100000) / 100000
  }

  const pool = CARDS.map((c) => c.id)
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    const tmp = pool[i]
    pool[i] = pool[j]
    pool[j] = tmp
  }
  return pool.slice(0, count).map((id) => ({ id, reversed: rand() < 0.3 }))
}

/** Ключ дня в местной таймзоне — «карта дня» живёт до полуночи. */
export function todayKey(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
